import React, { type ReactNode, type SFC, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import type {
  IMerchantGroupContext,
  MerchantGroupQueryParam,
} from "src/merchantgroups/objects/MerchantGroupQueryParam";
import {
  type IMerchantAbstract,
  type IMerchantAbstractNew,
  Merchant,
} from "src/merchants/objects/Merchant";
import type { MerchantQueryParam } from "src/merchants/objects/MerchantQueryParam";
import {
  getMerchantAbstract,
  getMerchantDetailViewPath,
} from "src/merchants/views/MerchantPaths";
import { Logo } from "src/shared/components/Logo";
import {
  type IRaangeLabelProps,
  type IRaangeLabelWithFAProps,
  RaangeLabel,
} from "src/shared/components/RaangeLabel";
import { BooleanStatusFormatter } from "src/shared/formatters/BooleanStatusFormatter";
import type ILinkable from "src/shared/formatters/ILinkable";
import {
  type IStatusFormatterProps,
  StatusFormatter,
} from "src/shared/formatters/StatusFormatter";
import { getSubscribersViewPath } from "src/subscribers/views/SubscriberPaths";

export const MerchantLabel = (props: IRaangeLabelWithFAProps) => {
  return (
    <RaangeLabel fa="store-alt" {...props}>
      <FormattedMessage id="merchants" />
    </RaangeLabel>
  );
};

export interface IMerchantFormatterProps extends Omit<IRaangeLabelProps, "fa"> {
  children: Merchant;
}

export type IMerchantLogoProps = Omit<IMerchantFormatterProps, "children"> &
  ILinkable & {
    size?: number;
    children: Merchant | string;
  };

export const MerchantLogo: SFC<IMerchantLogoProps> = (props) => {
  const { children, size, hasLink = false } = props;
  const logoUrl = children instanceof Merchant ? children.logoUrl : children;

  if (!logoUrl) {
    return null;
  }

  const merchantLogo = <Logo url={logoUrl} size={size} />;

  if (hasLink && children instanceof Merchant) {
    const link = getMerchantDetailViewPath(children);
    return <Link to={link}>{merchantLogo}</Link>;
  } else {
    return merchantLogo;
  }
};

export interface IMerchantNameProps extends ILinkable {
  children: IMerchantAbstract | IMerchantAbstractNew;
  merchantGroup?: MerchantGroupQueryParam;
  linkHandler?: (
    merchant: MerchantQueryParam,
    merchantGroup?: MerchantGroupQueryParam,
  ) => string;
}

export const MerchantName: SFC<IMerchantNameProps> = (props) => {
  const { children, hasLink = false, merchantGroup, ...rest } = props;
  const merchantName = (
    <RaangeLabel fa="store-alt" {...rest}>
      {children.merchantName}
    </RaangeLabel>
  );
  if (hasLink) {
    const { linkHandler = getMerchantDetailViewPath } = props;
    const link = linkHandler(getMerchantAbstract(children), merchantGroup);
    return <Link to={link}>{merchantName}</Link>;
  } else {
    return merchantName;
  }
};

export const MerchantCreate: SFC<Omit<IRaangeLabelProps, "fa">> = (props) => {
  return (
    <RaangeLabel fa="plus" {...props}>
      <FormattedMessage id="merchants.create" />
    </RaangeLabel>
  );
};

export const MerchantEdit = (props: IRaangeLabelWithFAProps) => {
  return (
    <RaangeLabel fa="pencil-alt" {...props}>
      <FormattedMessage id="merchants.edit" />
    </RaangeLabel>
  );
};

export const MerchantSubscribers = ({
  children: merchant,
  merchantGroup,
  ...rest
}: IMerchantFormatterProps & IMerchantGroupContext) => {
  const subscriberLink = useMemo(() => {
    return getSubscribersViewPath({
      // @ts-ignore
      merchantGroupUuid: merchantGroup?.merchantGroupUUID ?? merchantGroup,
      merchantUuid: merchant.merchantUUID,
    });
  }, [merchant.merchantUUID, merchantGroup]);
  return (
    <Link to={subscriberLink}>
      <RaangeLabel fa="building" {...rest}>
        {merchant.merchantName}
      </RaangeLabel>
    </Link>
  );
};

export const MerchantStatus = (props: IMerchantFormatterProps) => {
  return (
    <BooleanStatusFormatter>
      {props.children.merchantStatus}
    </BooleanStatusFormatter>
  );
};

export interface MerchantNumberStatusProps extends IMerchantFormatterProps {
  hideLabel?: boolean;
}

export const MerchantNumberStatus = ({
  hideLabel,
  children: merchant,
}: MerchantNumberStatusProps) => {
  const { text, status } = useMemo<{
    text: ReactNode;
    status: IStatusFormatterProps["status"];
  }>(() => {
    switch (merchant.mobileVirtualNumberStatus) {
      case "NOT_VERIFIED":
        return {
          text: (
            <FormattedMessage id="merchants.mobileVirtualNumberStatus.notVerified" />
          ),
          status: "inactive",
        };
      case "VERIFIED":
        return {
          text: (
            <FormattedMessage id="merchants.mobileVirtualNumberStatus.verified" />
          ),
          status: "active",
        };
      case "ERROR":
        return {
          text: (
            <FormattedMessage id="merchants.mobileVirtualNumberStatus.error" />
          ),
          status: "warning",
        };
      default:
        return {
          text: <FormattedMessage id="ui.na" />,
          status: "inactive",
        };
    }
  }, [merchant.mobileVirtualNumberStatus]);
  return (
    <>
      <StatusFormatter status={status}>{!hideLabel && text}</StatusFormatter>
    </>
  );
};
